import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Buttons/Button';
import { getFirestore, collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import ReactMarkdown from 'react-markdown';
import ImageUpload from '../components/ImageUpload';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { encode } from "blurhash";
import { useData } from '../contexts/DataContext';

const getImageData = (image: HTMLImageElement): Promise<ImageData> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext("2d");
    if (!context) reject(new Error("Could not get canvas context"));
    
    context!.drawImage(image, 0, 0);
    resolve(context!.getImageData(0, 0, image.width, image.height));
  });
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const SubmitBlog: React.FC = () => {
  const [searchParams] = useSearchParams();
  const editBlogId = searchParams.get("edit");
  const { blogsData } = useData();
  
  const [blogTitle, setBlogTitle] = useState(() => {
    if (editBlogId) {
      const blog = blogsData.find(blog => blog.id === editBlogId);
      return blog?.title || '';
    }
    return '';
  });
  
  const [content, setContent] = useState(() => {
    if (editBlogId) {
      const blog = blogsData.find(blog => blog.id === editBlogId);
      return blog?.content || '';
    }
    return '';
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUploadStatus, setImageUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(() => {
    if (editBlogId) {
      const blog = blogsData.find(blog => blog.id === editBlogId);
      return blog?.header_image || null;
    }
    return null;
  });
  const [headerImage, setHeaderImage] = useState(() => {
    if (editBlogId) {
      const blog = blogsData.find(blog => blog.id === editBlogId);
      return blog?.header_image || '';
    }
    return '';
  });

  const [headerImageBlurHash, setHeaderImageBlurHash] = useState(() => {
    if (editBlogId) {
      const blog = blogsData.find(blog => blog.id === editBlogId);
      return blog?.header_image_blur_hash || '';
    }
    return '';
  });
  
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { app, storage } = useFirebase();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_') // Replace non-alphanumeric chars with underscore
      .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
      .substring(0, 50); // Limit length
  };

  const handleImageUpload = async (file: File) => {
    setImageUploadStatus('uploading');
    const imageRef = ref(storage, `blogs/header_images/${Date.now()}_${file.name}`);
    
    try {
      const snapshot = await uploadBytes(imageRef, file);
      const url = await getDownloadURL(snapshot.ref);

      // Generate blurhash
      const objectUrl = URL.createObjectURL(file);
      const img = await loadImage(objectUrl);
      const imageData = await getImageData(img);
      
      const blurhash = encode(
        imageData.data,
        imageData.width,
        imageData.height,
        4,  // x components
        3   // y components
      );

      URL.revokeObjectURL(objectUrl);
      
      setPreviewImageUrl(url);
      setHeaderImage(url);
      setHeaderImageBlurHash(blurhash);
      setImageUploadStatus('success');
    } catch (error) {
      console.error("Error uploading image:", error);
      setImageUploadStatus('error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be logged in to submit a blog');
      return;
    }

    if (!blogTitle.trim() || !content.trim()) {
      setError('Title and content are required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const db = getFirestore(app);
      const slug = generateSlug(blogTitle);
      const blogData = {
        title: blogTitle.trim(),
        content: content.trim(),
        author: currentUser.displayName,
        author_id: currentUser.uid,
        slug,
        header_image: headerImage,
        header_image_blur_hash: headerImageBlurHash,
        approved: false,
      };

      if (editBlogId) {
        // Update existing blog
        const docRef = doc(db, 'blogs', editBlogId);
        await updateDoc(docRef, {
          ...blogData,
          updated_at: new Date().toISOString(),
        });
        navigate('/blog');
      } else {
        // Create new blog
        const blogsCollection = collection(db, 'blogs');
        const docRef = await addDoc(blogsCollection, {
          ...blogData,
          created_at: new Date().toISOString(),
          comments: [],
          likes: [],
        });
        await updateDoc(docRef, { id: docRef.id });
        setShowSuccessModal(true);
      }
    } catch (err) {
      setError('Failed to submit blog. Please try again.');
      console.error('Error submitting blog:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p>Please log in to submit a blog post.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">
        {editBlogId ? 'Edit Blog Post' : 'Submit a Blog Post'}
      </h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium">Header Image</label>
          <div className="relative">
            <div className="w-32 h-32 bg-gray-200 rounded-lg m-4 relative" />
            <ImageUpload
              onFileSelect={handleImageUpload}
              currentImage={previewImageUrl || headerImage}
              maxSize={500}
              imgClasses="absolute -top-2 left-0 w-32 h-32 rounded-lg m-4"
            />
          </div>
          {imageUploadStatus === 'uploading' && (
            <p className="text-blue-500 text-sm">Uploading image...</p>
          )}
          {imageUploadStatus === 'error' && (
            <p className="text-red-500 text-sm">Failed to upload image. Please try again.</p>
          )}
        </div>

        <div>
          <label htmlFor="blog-post-title" className="block text-sm font-medium text-gray-700 mb-2">
            Title
          </label>
          <input
            type="text"
            id="blog-post-title"
            name="blog-post-title"
            value={blogTitle}
            onChange={(e) => setBlogTitle(e.target.value)}
            className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-muted-sage relative z-10"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-muted-sage min-h-[400px]"
            placeholder="Write your blog post here..."
            required
          />
        </div>

        {content && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Preview</h2>
            <div className="prose prose-lg max-w-none border rounded-md p-6 bg-gray-50 markdown">
              <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => (
                    <a
                      {...props}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <Button
            onClick={() => navigate('/blog')}
            color="clay"
            type="button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting 
              ? 'Submitting...' 
              : editBlogId 
                ? 'Edit Blog' 
                : 'Submit Blog'
            }
          </Button>
        </div>
      </form>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-xl font-semibold mb-4">Thank You!</h3>
            <p className="mb-6">Thanks for submitting a blog post. Our team will review and get back to you shortly.</p>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/blog');
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmitBlog; 